// src/lib/components/atoms/DateTimeDisplay/DateTimeDisplayAtom.tsx
import React from 'react';
import {
  formatDateTime,
  toIsoString,
  DateTimeParts,
  DisplayFormat,
  FormatOptions,
} from '@center-platform/spheres/identity';
import GenericDisplayAtom, {
  DisplayLine,
} from '../GenericDisplay/GenericDisplayAtom';
import './DateTimeDisplayAtom.scss';

export interface DateTimeDisplayAtomProps {
  /** Date to display */
  date: Date | null;
  /** Time in "HH:mm" format */
  time: string;
  /** Timezone identifier */
  timezone: string;
  /** Display format */
  format?: DisplayFormat;
  /** Custom format string (date‐only portion) */
  customFormat?: string;
  /** Whether to show the timezone name (for full/custom modes) */
  showTimezone?: boolean;
  /** Whether to show ISO string */
  showIso?: boolean;
  /** Optional CSS class name */
  className?: string;
}

/**
 * DateTimeDisplayAtom
 *
 * Displays a formatted date/time/timezone string, plus optional
 * separate timezone and ISO lines via GenericDisplayAtom.
 */
export const DateTimeDisplayAtom: React.FC<DateTimeDisplayAtomProps> = ({
  date,
  time,
  timezone,
  format = 'full',
  customFormat,
  showTimezone = true,
  showIso = false,
  className = '',
}) => {
  if (!date || !time) {
    return (
      <GenericDisplayAtom
        main="No date/time selected"
        variant="minimal"
        className={className}
      />
    );
  }

  // Build the shared parts object
  const parts: DateTimeParts = { date, time, timezone };
  const fmtOpts: FormatOptions = { customFormat, showTimezone };

  // Main line
  const main = formatDateTime(parts, format, fmtOpts);

  // Meta lines
  const meta: DisplayLine[] = [];
  if (showTimezone && format !== 'compact') {
    meta.push({ label: 'Timezone:', value: timezone });
  }
  if (showIso) {
    meta.push({ label: 'ISO:', value: toIsoString(parts) });
  }

  return (
    <GenericDisplayAtom
      main={main}
      meta={meta}
      variant={format === 'compact' ? 'compact' : 'full'}
      className={className}
    />
  );
};

export default DateTimeDisplayAtom;
